import { Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GridModule, GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { IconsModule } from '@progress/kendo-angular-icons';
import { KENDO_ICONS } from '@progress/kendo-angular-icons';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import {
  Change,
  ChangeType,
  ChangeState,
  ChangePriority,
  ChangeSearchCriteria,
  WorkflowState,
  ChangeTransitionResult
} from '../../models/file-vault.models';

export interface ChangeListAction {
  action: 'view' | 'edit' | 'submit' | 'delete';
  change: Change;
}

@Component({
  selector: 'fv-change-list',
  standalone: true,
  template: `
    <div class="change-list-container">
      <!-- Toolbar -->
      <div class="toolbar">
        <div class="toolbar-left">
          <div class="filter-group">
            <input kendoTextBox
                   [(ngModel)]="searchText"
                   placeholder="Search changes..."
                   class="search-input"
                   (keyup.enter)="search()" />
            <button kendoButton [look]="'flat'" (click)="search()">Search</button>
          </div>

          <kendo-dropdownlist
            [(ngModel)]="filterType"
            [data]="changeTypes"
            textField="text"
            valueField="value"
            [valuePrimitive]="true"
            (valueChange)="onFilterChange()"
            class="filter-dropdown">
          </kendo-dropdownlist>

          <kendo-dropdownlist
            [(ngModel)]="filterState"
            [data]="changeStates"
            textField="text"
            valueField="value"
            [valuePrimitive]="true"
            (valueChange)="onFilterChange()"
            class="filter-dropdown">
          </kendo-dropdownlist>

          <kendo-dropdownlist
            [(ngModel)]="filterPriority"
            [data]="priorities"
            textField="text"
            valueField="value"
            [valuePrimitive]="true"
            (valueChange)="onFilterChange()"
            class="filter-dropdown">
          </kendo-dropdownlist>
        </div>

        <div class="toolbar-right">
          <button kendoButton [themeColor]="'primary'" (click)="onCreateClick()" *ngIf="showCreateButton">
            New Change
          </button>
          <button kendoButton [look]="'flat'" (click)="refresh()">
            Refresh
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div class="loading" *ngIf="isLoading">Loading changes...</div>

      <!-- Grid -->
      <kendo-grid
        *ngIf="!isLoading"
        [data]="gridData"
        [pageSize]="pageSize"
        [skip]="skip"
        [pageable]="true"
        [sortable]="true"
        [sort]="sort"
        (pageChange)="onPageChange($event)"
        (sortChange)="onSortChange($event)"
        [selectable]="{ mode: 'single' }"
        (selectionChange)="onSelectionChange($event)"
        class="change-grid">

        <kendo-grid-column field="changeNumber" title="Change #" [width]="120">
          <ng-template kendoGridCellTemplate let-dataItem>
            <a class="change-link" (click)="onViewClick(dataItem)">{{ dataItem.changeNumber }}</a>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="changeType" title="Type" [width]="80">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="type-badge" [ngClass]="getTypeBadgeClass(dataItem.changeType)">
              {{ getTypeLabel(dataItem.changeType) }}
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="title" title="Title" [width]="250">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="title-text" [title]="dataItem.title">{{ dataItem.title }}</span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="state" title="State" [width]="120">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="state-badge" [ngClass]="getStateBadgeClass(dataItem.state)">
              {{ getStateLabel(dataItem.state) }}
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column title="Workflow" [width]="220">
          <ng-template kendoGridCellTemplate let-dataItem>
            <ng-container *ngIf="workflowSummaries[dataItem.id] as summary">
              <div class="workflow-cell">
                <span class="workflow-state">{{ summary.stateLabel }}</span>
                <span class="workflow-step" *ngIf="summary.stepName">• {{ summary.stepName }}</span>
                <div class="workflow-assignment" *ngIf="summary.assignedTo || summary.assignedToRole">
                  Assigned: {{ summary.assignedTo || summary.assignedToRole }}
                </div>
              </div>
            </ng-container>
            <span class="workflow-placeholder" *ngIf="!workflowSummaries[dataItem.id]">-</span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="priority" title="Priority" [width]="90">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="priority-indicator" [ngClass]="getPriorityClass(dataItem.priority)">
              {{ getPriorityLabel(dataItem.priority) }}
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="raisedBy" title="Raised By" [width]="120">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.raisedBy }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="raisedAt" title="Raised" [width]="100">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.raisedAt | date:'shortDate' }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="dueDate" title="Due" [width]="100">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span [ngClass]="{ 'overdue': isOverdue(dataItem) }">
              {{ dataItem.dueDate ? (dataItem.dueDate | date:'shortDate') : '-' }}
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column title="Actions" [width]="160" *ngIf="showActions">
          <ng-template kendoGridCellTemplate let-dataItem>
            <div class="action-buttons">
              <button kendoButton fillMode="flat" (click)="onViewClick(dataItem)" title="View" aria-label="View">
                <kendo-icon name="eye"></kendo-icon>
              </button>
              <button kendoButton fillMode="flat" (click)="onEditClick(dataItem)"
                      *ngIf="canEdit(dataItem)" title="Edit" aria-label="Edit">
                <kendo-icon name="pencil"></kendo-icon>
              </button>
              <button kendoButton fillMode="flat"
                      (click)="confirmSubmit(dataItem)"
                      *ngIf="canSubmit(dataItem)" title="Submit" aria-label="Submit"
                      [disabled]="isProcessing"
                      class="btn-success">
                <kendo-icon name="check"></kendo-icon>
              </button>
              <button kendoButton fillMode="flat"
                      (click)="confirmDelete(dataItem)"
                      *ngIf="canDelete(dataItem)" title="Delete" aria-label="Delete"
                      [disabled]="isProcessing"
                      class="btn-danger">
                <kendo-icon name="trash"></kendo-icon>
              </button>
            </div>
          </ng-template>
        </kendo-grid-column>
      </kendo-grid>

      <!-- Empty state -->
      <div class="empty-state" *ngIf="!isLoading && gridData.total === 0">
        <p>No changes found</p>
        <button kendoButton [themeColor]="'primary'" (click)="onCreateClick()" *ngIf="showCreateButton">
          Create First Change
        </button>
      </div>

      <!-- Submit Confirmation Dialog -->
      <kendo-dialog *ngIf="showSubmitConfirm"
                    title="Submit Change"
                    [width]="420"
                    (close)="showSubmitConfirm = false">
        <p>Submit <strong>{{ pendingAction?.changeNumber }}</strong> for review?</p>
        <p class="dialog-hint">This will start a workflow and the change can no longer be edited.</p>
        <div class="dialog-error" *ngIf="actionError">{{ actionError }}</div>
        <kendo-dialog-actions>
          <button kendoButton (click)="showSubmitConfirm = false" [disabled]="isProcessing">Cancel</button>
          <button kendoButton [themeColor]="'primary'" (click)="executeSubmit()" [disabled]="isProcessing">
            {{ isProcessing ? 'Submitting...' : 'Submit' }}
          </button>
        </kendo-dialog-actions>
      </kendo-dialog>

      <!-- Delete Confirmation Dialog -->
      <kendo-dialog *ngIf="showDeleteConfirm"
                    title="Delete Change"
                    [width]="420"
                    (close)="showDeleteConfirm = false">
        <p>Are you sure you want to delete <strong>{{ pendingAction?.changeNumber }}</strong>?</p>
        <p class="dialog-hint warning">This action cannot be undone.</p>
        <div class="dialog-error" *ngIf="actionError">{{ actionError }}</div>
        <kendo-dialog-actions>
          <button kendoButton (click)="showDeleteConfirm = false" [disabled]="isProcessing">Cancel</button>
          <button kendoButton [themeColor]="'error'" (click)="executeDelete()" [disabled]="isProcessing">
            {{ isProcessing ? 'Deleting...' : 'Delete' }}
          </button>
        </kendo-dialog-actions>
      </kendo-dialog>
    </div>
  `,
  styles: [`
    .change-list-container {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px;
      background: var(--fv-surface-hover);
      border-bottom: 1px solid var(--fv-border);
      flex-wrap: wrap;
      gap: 8px;
    }

    .toolbar-left, .toolbar-right {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .filter-group {
      display: flex;
      gap: 4px;
    }

    .search-input {
      width: 200px;
    }

    .filter-dropdown {
      width: 120px;
    }

    .loading {
      padding: 24px;
      text-align: center;
      color: var(--fv-text-secondary);
    }

    .change-grid {
      flex: 1;
      font-size: 13px;
    }

    .change-link {
      color: var(--fv-text-accent);
      cursor: pointer;
      font-family: monospace;
      font-weight: 500;
    }

    .change-link:hover {
      text-decoration: underline;
    }

    .title-text {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .type-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .type-badge.ecr { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .type-badge.eco { background: var(--fv-status-warning-bg); color: var(--fv-status-warning-fg); }
    .type-badge.ecn { background: var(--fv-status-purple-bg); color: var(--fv-status-purple-fg); }
    .type-badge.deviation { background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }

    .state-badge {
      display: inline-block;
      padding: 3px 8px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 500;
    }

    .state-badge.draft { background: var(--fv-status-draft-bg); color: var(--fv-status-draft-fg); }
    .state-badge.submitted { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .state-badge.in-review { background: var(--fv-status-warning-bg); color: var(--fv-status-warning-fg); }
    .state-badge.approved { background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .state-badge.rejected { background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }
    .state-badge.in-implementation { background: var(--fv-status-indigo-bg); color: var(--fv-status-indigo-fg); }
    .state-badge.implemented { background: var(--fv-status-teal-bg); color: var(--fv-status-teal-fg); }
    .state-badge.closed { background: var(--fv-status-bluegrey-bg); color: var(--fv-status-bluegrey-fg); }
    .state-badge.cancelled { background: var(--fv-surface-alt); color: var(--fv-text-muted); }

    .priority-indicator {
      font-size: 11px;
      font-weight: 500;
    }

    .priority-indicator.low { color: var(--fv-status-success-fg); }
    .priority-indicator.medium { color: var(--fv-status-warning-fg); }
    .priority-indicator.high { color: var(--fv-status-error-fg); }
    .priority-indicator.critical { color: var(--fv-status-error-fg); font-weight: 700; }

    .workflow-cell {
      display: flex;
      flex-direction: column;
      gap: 2px;
      font-size: 12px;
      color: var(--fv-text);
    }

    .workflow-state {
      font-weight: 600;
    }

    .workflow-step {
      color: var(--fv-text-muted);
    }

    .workflow-assignment {
      font-size: 11px;
      color: var(--fv-text-muted);
    }

    .workflow-placeholder {
      color: var(--fv-text-muted);
    }

    .overdue {
      color: var(--fv-status-error-fg);
      font-weight: 500;
    }

    .action-buttons {
      display: flex;
      gap: 4px;
    }

    .btn-success { color: #28a745; }
    .btn-danger { color: #dc3545; }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 48px;
      color: var(--fv-text-secondary);
    }

    .empty-state p {
      margin-bottom: 16px;
    }

    .dialog-hint {
      font-size: 13px;
      color: var(--fv-text-secondary);
      margin-top: -8px;
    }

    .dialog-hint.warning {
      color: var(--fv-status-error-fg);
    }

    .dialog-error {
      margin-top: 8px;
      font-size: 13px;
      color: var(--fv-status-error-fg);
      padding: 10px 12px;
      background: var(--fv-status-error-bg);
      border: 1px solid var(--fv-status-error-border);
      border-radius: 4px;
    }
  `],
  imports: [CommonModule, FormsModule, GridModule, ButtonsModule, DialogModule, DropDownsModule, InputsModule, IconsModule, KENDO_ICONS]
})
export class ChangeListComponent implements OnInit {
  private api = inject(FileVaultApiService);

  /** Show create button */
  @Input() showCreateButton = true;

  /** Show action buttons in grid */
  @Input() showActions = true;

  /** Pre-filter by state */
  @Input() initialState?: ChangeState;

  /** Pre-filter by type */
  @Input() initialType?: ChangeType;

  /** Emitted when create is clicked */
  @Output() createClick = new EventEmitter<void>();

  /** Emitted when an action is performed */
  @Output() actionClick = new EventEmitter<ChangeListAction>();

  /** Emitted when a change is selected */
  @Output() selectionChange = new EventEmitter<Change | null>();

  gridData: GridDataResult = { data: [], total: 0 };
  isLoading = false;
  isProcessing = false;
  workflowSummaries: Record<string, {
    stateLabel: string;
    stepName?: string;
    assignedTo?: string;
    assignedToRole?: string;
    isLoading?: boolean;
  }> = {};
  pageSize = 20;
  skip = 0;
  sort: SortDescriptor[] = [{ field: 'raisedAt', dir: 'desc' }];

  searchText = '';
  filterType: ChangeType | null = null;
  filterState: ChangeState | null = null;
  filterPriority: ChangePriority | null = null;

  // Confirmation dialog state
  showSubmitConfirm = false;
  showDeleteConfirm = false;
  pendingAction: Change | null = null;
  actionError = '';

  changeTypes = [
    { text: 'All Types', value: null },
    { text: 'ECR', value: ChangeType.ECR },
    { text: 'ECO', value: ChangeType.ECO },
    { text: 'ECN', value: ChangeType.ECN },
    { text: 'Deviation', value: ChangeType.Deviation }
  ];

  changeStates = [
    { text: 'All States', value: null },
    { text: 'Draft', value: ChangeState.Draft },
    { text: 'Submitted', value: ChangeState.Submitted },
    { text: 'In Review', value: ChangeState.InReview },
    { text: 'Approved', value: ChangeState.Approved },
    { text: 'Rejected', value: ChangeState.Rejected },
    { text: 'In Implementation', value: ChangeState.InImplementation },
    { text: 'Implemented', value: ChangeState.Implemented },
    { text: 'Closed', value: ChangeState.Closed },
    { text: 'Cancelled', value: ChangeState.Cancelled }
  ];

  priorities = [
    { text: 'All Priorities', value: null },
    { text: 'Low', value: ChangePriority.Low },
    { text: 'Medium', value: ChangePriority.Medium },
    { text: 'High', value: ChangePriority.High },
    { text: 'Critical', value: ChangePriority.Critical }
  ];

  ngOnInit(): void {
    if (this.initialState !== undefined) {
      this.filterState = this.initialState;
    }
    if (this.initialType !== undefined) {
      this.filterType = this.initialType;
    }
    this.loadChanges();
  }

  async loadChanges(): Promise<void> {
    this.isLoading = true;

    try {
      const criteria: ChangeSearchCriteria = {
        page: Math.floor(this.skip / this.pageSize) + 1,
        pageSize: this.pageSize,
        sortBy: this.sort[0]?.field,
        sortDescending: this.sort[0]?.dir === 'desc'
      };

      if (this.searchText) criteria.searchText = this.searchText;
      if (this.filterType !== null) criteria.changeType = this.filterType;
      if (this.filterState !== null) criteria.state = this.filterState;
      if (this.filterPriority !== null) criteria.priority = this.filterPriority;

      const result = await this.api.searchChanges(criteria).toPromise();

      this.gridData = {
        data: result?.items ?? [],
        total: result?.totalCount ?? 0
      };
      await this.loadWorkflowSummaries(this.gridData.data as Change[]);
    } catch (error) {
      console.error('Failed to load changes', error);
      this.gridData = { data: [], total: 0 };
    } finally {
      this.isLoading = false;
    }
  }

  async loadWorkflowSummaries(changes: Change[]): Promise<void> {
    this.workflowSummaries = {};

    const tasks = changes.map(async change => {
      this.workflowSummaries[change.id] = { stateLabel: 'Loading...', isLoading: true };
      try {
        let instanceId = change.workflowInstanceId;
        if (!instanceId) {
          const instance = await this.api.getWorkflowForEntity('Change', change.id).toPromise();
          instanceId = instance?.id;
        }

        if (!instanceId) {
          this.workflowSummaries[change.id] = { stateLabel: 'No workflow' };
          return;
        }

        const progress = await this.api.getWorkflowProgress(instanceId).toPromise();
        if (!progress) {
          this.workflowSummaries[change.id] = { stateLabel: 'No workflow' };
          return;
        }

        const step = progress.steps?.find(s => s.stepIndex === progress.currentStepIndex);
        this.workflowSummaries[change.id] = {
          stateLabel: this.getWorkflowStateLabel(progress.state),
          stepName: step?.stepName,
          assignedTo: step?.assignedTo,
          assignedToRole: step?.assignedToRole
        };
      } catch (error) {
        console.error('Failed to load workflow summary', error);
        this.workflowSummaries[change.id] = { stateLabel: 'Unavailable' };
      }
    });

    await Promise.all(tasks);
  }

  search(): void {
    this.skip = 0;
    this.loadChanges();
  }

  onFilterChange(): void {
    this.skip = 0;
    this.loadChanges();
  }

  onPageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take;
    this.loadChanges();
  }

  onSortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.loadChanges();
  }

  onSelectionChange(event: any): void {
    const selected = event.selectedRows?.[0]?.dataItem;
    this.selectionChange.emit(selected ?? null);
  }

  onCreateClick(): void {
    console.log('[ChangeList] Create clicked');
    this.createClick.emit();
  }

  onViewClick(change: Change): void {
    this.actionClick.emit({ action: 'view', change });
  }

  onEditClick(change: Change): void {
    this.actionClick.emit({ action: 'edit', change });
  }

  onSubmitClick(change: Change): void {
    this.actionClick.emit({ action: 'submit', change });
  }

  // ── Confirmation dialogs ──

  confirmSubmit(change: Change): void {
    this.pendingAction = change;
    this.actionError = '';
    this.showSubmitConfirm = true;
  }

  confirmDelete(change: Change): void {
    this.pendingAction = change;
    this.actionError = '';
    this.showDeleteConfirm = true;
  }

  async executeSubmit(): Promise<void> {
    if (!this.pendingAction) return;

    this.isProcessing = true;
    this.actionError = '';
    try {
      const result = await this.api.submitChange(this.pendingAction.id).toPromise();
      if (result?.success) {
        this.actionClick.emit({ action: 'submit', change: this.pendingAction });
        this.showSubmitConfirm = false;
        this.pendingAction = null;
        await this.loadChanges();
      } else {
        this.actionError = result?.errorMessage || 'Submit failed. Please try again.';
      }
    } catch (error: any) {
      this.actionError = error?.error?.message || error?.error?.error || error?.message || 'Failed to submit change.';
    } finally {
      this.isProcessing = false;
    }
  }

  async executeDelete(): Promise<void> {
    if (!this.pendingAction) return;

    this.isProcessing = true;
    this.actionError = '';
    try {
      await this.api.deleteChange(this.pendingAction.id).toPromise();
      this.actionClick.emit({ action: 'delete', change: this.pendingAction });
      this.showDeleteConfirm = false;
      this.pendingAction = null;
      await this.loadChanges();
    } catch (error: any) {
      this.actionError = error?.error?.message || error?.error?.error || error?.message || 'Failed to delete change.';
    } finally {
      this.isProcessing = false;
    }
  }

  refresh(): void {
    this.loadChanges();
  }

  canEdit(change: Change): boolean {
    return change.state === ChangeState.Draft || change.state === ChangeState.Rejected;
  }

  canSubmit(change: Change): boolean {
    return change.state === ChangeState.Draft;
  }

  canDelete(change: Change): boolean {
    return change.state === ChangeState.Draft;
  }

  isOverdue(change: Change): boolean {
    if (!change.dueDate) return false;
    const dueDate = new Date(change.dueDate);
    const now = new Date();
    return dueDate < now && change.state !== ChangeState.Closed && change.state !== ChangeState.Cancelled;
  }

  getTypeLabel(type: ChangeType): string {
    switch (type) {
      case ChangeType.ECR: return 'ECR';
      case ChangeType.ECO: return 'ECO';
      case ChangeType.ECN: return 'ECN';
      case ChangeType.Deviation: return 'DEV';
      default: return 'Unknown';
    }
  }

  getTypeBadgeClass(type: ChangeType): string {
    switch (type) {
      case ChangeType.ECR: return 'ecr';
      case ChangeType.ECO: return 'eco';
      case ChangeType.ECN: return 'ecn';
      case ChangeType.Deviation: return 'deviation';
      default: return '';
    }
  }

  getStateLabel(state: ChangeState): string {
    switch (state) {
      case ChangeState.Draft: return 'Draft';
      case ChangeState.Submitted: return 'Submitted';
      case ChangeState.InReview: return 'In Review';
      case ChangeState.Approved: return 'Approved';
      case ChangeState.Rejected: return 'Rejected';
      case ChangeState.InImplementation: return 'Implementing';
      case ChangeState.Implemented: return 'Implemented';
      case ChangeState.Closed: return 'Closed';
      case ChangeState.Cancelled: return 'Cancelled';
      default: return 'Unknown';
    }
  }

  getStateBadgeClass(state: ChangeState): string {
    switch (state) {
      case ChangeState.Draft: return 'draft';
      case ChangeState.Submitted: return 'submitted';
      case ChangeState.InReview: return 'in-review';
      case ChangeState.Approved: return 'approved';
      case ChangeState.Rejected: return 'rejected';
      case ChangeState.InImplementation: return 'in-implementation';
      case ChangeState.Implemented: return 'implemented';
      case ChangeState.Closed: return 'closed';
      case ChangeState.Cancelled: return 'cancelled';
      default: return '';
    }
  }

  getPriorityLabel(priority: ChangePriority): string {
    switch (priority) {
      case ChangePriority.Low: return 'Low';
      case ChangePriority.Medium: return 'Medium';
      case ChangePriority.High: return 'High';
      case ChangePriority.Critical: return 'Critical';
      default: return 'Unknown';
    }
  }

  getPriorityClass(priority: ChangePriority): string {
    switch (priority) {
      case ChangePriority.Low: return 'low';
      case ChangePriority.Medium: return 'medium';
      case ChangePriority.High: return 'high';
      case ChangePriority.Critical: return 'critical';
      default: return '';
    }
  }

  getWorkflowStateLabel(state?: WorkflowState): string {
    switch (state) {
      case WorkflowState.Pending: return 'Pending';
      case WorkflowState.InProgress: return 'In Progress';
      case WorkflowState.Completed: return 'Completed';
      case WorkflowState.Cancelled: return 'Cancelled';
      case WorkflowState.Failed: return 'Failed';
      default: return 'No workflow';
    }
  }
}
